import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { Weekday } from '@chancenwerk/db/enums/enums.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import { DayAvailable, type Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';

@customElement('chancen-component-time-matrix')
export class ChancenComponentTimeMatrix extends ChemistryLitElement {
  @property({ attribute: false }) public daysAvailable = new RXController<Maybe<DayAvailable[]>>(this, []);
  @property({ attribute: false }) public dateOffers = new RXController<Maybe<DateTime[]>>(this, null);

  override styles() {
    return [
      {
        '.button:not([active])': {
          position: 'relative',
          cursor: 'pointer',
          userSelect: 'none',
          textAlign: 'center',
          transitionDuration: '0.4s',
          '-webkit-transition-duration': '0.4s',
          background: 'white',
          border: `1px solid ${this.colors.primary}`,
        },
        '.button:not([active]):hover': {
          transitionDuration: '0.1s',
          backgroundColor: this.colors.primary,
          color: this.colors.white,
        },
        '.button[active]': {
          position: 'relative',
          color: 'white',
          cursor: 'pointer',
          userSelect: 'none',
          textAlign: 'center',
          transitionDuration: '0.4s',
          '-webkit-transition-duration': '0.4s',
          background: this.colors.primary,
        },
        '.button[active]:hover': {
          transitionDuration: '0.1s',
          backgroundColor: this.colors.primary,
          color: this.colors.white,
        },
        '.button': {
          color: 'black',
          border: 'none',
          position: 'relative',
          cursor: 'pointer',
          userSelect: 'none',
          textAlign: 'center',
          transitionDuration: '0.4s',
          '-webkit-transition-duration': '0.4s',
          background: 'white',
        },
        '.button:hover': {
          transitionDuration: '0.1s',
          backgroundColor: this.colors.primary,
          color: this.colors.white,
        },
      },
    ];
  }

  private _toggleTimeOnDayAvailable(weekday: Weekday, time: DateTime) {
    if (!this.daysAvailable.value) {
      return;
    }

    const dayAvailable = this.daysAvailable.value.find(d => d.day === weekday);
    if (!dayAvailable) {
      this.daysAvailable.value.push(
        new DayAvailable({
          day: weekday,
          timesAvailable: [time],
        }),
      );
      return;
    }

    if (dayAvailable.timesAvailable.some(t => t.toFormat('HH:mm') === time.toFormat('HH:mm'))) {
      dayAvailable.timesAvailable = dayAvailable.timesAvailable.filter(
        t => t.toFormat('HH:mm') !== time.toFormat('HH:mm'),
      );
    } else {
      dayAvailable.timesAvailable.push(time);
    }
  }

  override render() {
    const weekdays = Object.values(Weekday).filter(d => d !== Weekday.SATURDAY && d !== Weekday.SUNDAY);
    if (!this.daysAvailable.value) return nothing;

    const dateOffers = this.dateOffers.value ?? [
      DateTime.now().startOf('day').plus({ hours: 16, minutes: 10 }),
      DateTime.now().startOf('day').plus({ hours: 17, minutes: 50 }),
    ];
    return html`
      <cr-flex space="md" ${css({ paddingTop: this.spacing.md })}>
        <cr-grid space="sm" columns="repeat(5, 1fr)">
          ${repeat(
            weekdays,
            key => key,
            (weekday, index) => html`<cr-text ${css({ textAlign: 'center' })} bold>${weekday}</cr-text>`,
          )}
          ${dateOffers.map(
            dateOffer => html`
              ${repeat(
                weekdays,
                key => key,
                (weekday, index) => {
                  const active = this.daysAvailable.value?.some(
                    d =>
                      d.day === weekday &&
                      d.timesAvailable.some(t => t.toFormat('HH:mm') === dateOffer.toFormat('HH:mm')),
                  );

                  return html`
                    <cr-button
                      ?active=${active}
                      class="button"
                      @click=${e => {
                        if (!this.daysAvailable.value) {
                          return;
                        }

                        this._toggleTimeOnDayAvailable(weekday, dateOffer);
                        this.dispatchEvent(
                          new CustomEvent('value-picked', { detail: { value: this.daysAvailable.value } }),
                        );
                      }}
                    >
                      ${dateOffer.toFormat('HH:mm')} - ${dateOffer.plus({ minute: 90 }).toFormat('HH:mm')}
                    </cr-button>
                  `;
                },
              )}
            `,
          )}
        </cr-grid>
      </cr-flex>
    `;
  }
}

@customElement('chancen-dialog-time-matrix')
export class ChancenDialogTimeMatrix extends ChromiumDialog<Student> {
  static override get element_name() {
    return 'chancen-dialog-time-matrix';
  }

  @property({ attribute: false }) public headingText: string | undefined = undefined;
  @property({ attribute: false }) public student: Student | undefined = undefined;
  @property({ attribute: false }) public dateOffers = new RXController<Maybe<DateTime[]>>(this, null);
  @property({ attribute: false }) public path = new RXController<Maybe<string[]>>(this, ['daysAvailable']);

  override heading =
    'Teile uns bitte mit, wann du für einen digitalen Intensivkurs Zeit hättest. Wir melden uns zeitnah mit einem Kursangebot per E-Mail oder Anruf.';

  override headingContent() {
    if (this.headingText) {
      return html`<cr-h4>${this.headingText}</cr-h4>`;
    }
    return html`<cr-h4>${this.heading}</cr-h4>`;
  }

  override footerContent() {
    return html`
      <cr-flex horizontal space="md" end>
        <cr-button @click=${() => this.close(this.student!)}>Speichern</cr-button>
      </cr-flex>
    `;
  }

  // TODO: typing
  private _getBaseObject() {
    if (!this.student || !this.path.value || this.path.value.length === 0) {
      throw new Error('Student or path not set');
    }

    let base = this.student;
    for (const path of this.path.value?.slice(0, -1) ?? []) {
      base = base[path];
    }
    return base;
  }

  private _getFinalObject() {
    return this._getBaseObject()[this.path.value?.slice(-1)[0] ?? ''];
  }

  override content() {
    return html`<chancen-component-time-matrix
      .dateOffers=${this.dateOffers.value}
      .daysAvailable=${this._getFinalObject()}
      @value-picked=${(e: ValueEvent<Maybe<DayAvailable[]>>) => {
        if (!this.student || !this.path.value || this.path.value.length === 0) {
          return;
        }

        this._getBaseObject()[this.path.value?.slice(-1)[0] ?? ''] = e.detail.value;
        this.requestUpdate();
      }}
    ></chancen-component-time-matrix>`;
  }

  override cancel(returnVal: any) {
    return super.cancel(this.student!);
  }

  override close(returnVal: any) {
    return super.close(this.student!);
  }
}
